import {
  HappycoWorkCategoriesQuery,
  HappycoWorkCategoriesQueryVariables,
  HappycoWorkCategoryFragment,
} from "gql/graphql";
import React, { memo, useCallback, useEffect, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { Select, SingleValue } from "chakra-react-select";
import { SystemStyleObject } from "@chakra-ui/react";
import colors from "app/colors";
import HAPPYCO_WORK_CATEGORIES_QUERY from "./_networking/happyco-work-categories";
import { SELECT_MENU_PORTAL_Z_INDEX } from "modules/providers/chakra-provider/chakra-portal-stack";

interface IProps {
  propertyId?: string;
  onWorkCategorySelected: (
    happycoWorkCategory?: SingleValue<HappycoWorkCategoryFragment>,
  ) => void;
}

const HappycoWorkOrderCategorySelector = ({
  propertyId,
  onWorkCategorySelected,
}: IProps) => {
  const { data } = useQuery<
    HappycoWorkCategoriesQuery,
    HappycoWorkCategoriesQueryVariables
  >(HAPPYCO_WORK_CATEGORIES_QUERY, {
    variables: propertyId ? { propertyId } : undefined,
    skip: !propertyId,
  });

  const [value, setValue] =
    React.useState<SingleValue<HappycoWorkCategoryFragment> | null>();

  useEffect(() => {
    setValue(null);
    onWorkCategorySelected(undefined);
  }, [onWorkCategorySelected, propertyId]);

  const onChange = (
    happycoWorkCategory?: SingleValue<HappycoWorkCategoryFragment>,
  ) => {
    setValue(happycoWorkCategory);
    onWorkCategorySelected(happycoWorkCategory);
  };

  const getOptionLabel = useCallback(
    (happycoWorkCategory: HappycoWorkCategoryFragment) =>
      happycoWorkCategory.name,
    [],
  );

  const getOptionValue = useCallback(
    (happycoWorkCategory: HappycoWorkCategoryFragment) =>
      happycoWorkCategory.happycoId,
    [],
  );

  const happycoWorkCategories = useMemo(
    () => data?.happycoWorkCategories || [],
    [data],
  );

  return (
    <Select
      options={happycoWorkCategories}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      placeholder="Select Category"
      noOptionsMessage={() =>
        "No work categories found for the selected property"
      }
      onChange={onChange}
      value={value}
      components={{
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null,
      }}
      chakraStyles={{
        placeholder: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          fontStyle: "italic",
          color: colors.placeholderText,
          fontSize: 14,
        }),
        container: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          width: "full",
          borderColor: colors.grayPrimary,
        }),
        control: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          minHeight: "34px",
        }),
      }}
      menuPortalTarget={document.body}
      styles={{
        menuPortal: (base) => ({
          ...base,
          zIndex: SELECT_MENU_PORTAL_Z_INDEX,
        }),
      }}
    />
  );
};

export default memo(HappycoWorkOrderCategorySelector);
